
import React, { useCallback, useState } from 'react'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { NumberInput } from '@/components/ui/number-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { Icon, type IconName } from '@/components/ui/icon'
import type { MergedParameter } from '@/lib/ai/parameter-merge'

/** Map uiType to icon — matches sidebar ParameterControl icons */
const UI_TYPE_ICONS: Record<string, IconName> = {
  slider: 'sliders-horizontal',
  number: 'hash',
  select: 'list',
  toggle: 'toggle-left',
  seed: 'dice-3',
  input: 'text',
  textarea: 'text',
  'aspect-ratio': 'ratio',
  'image-size': 'maximize',
  'select-or-input': 'list',
}

// ============================================================================
// SHARED
// ============================================================================

interface CompactControlProps {
  label: string
  value: unknown
  onChange: (value: unknown) => void
  parameter: MergedParameter
}

/** Shared row wrapper — consistent height, hover state, nodrag */
function ControlRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 min-h-[28px] rounded-md px-1 -mx-1 nodrag nopan">
      {children}
    </div>
  )
}

function CompactLabel({ text, title, icon }: { text: string; title?: string; icon?: IconName }) {
  return (
    <div className="flex items-center gap-1.5 truncate" title={title || text}>
      {icon && <Icon name={icon} className="h-3 w-3 shrink-0 text-muted-foreground" />}
      <span className="text-[11px] font-mono font-medium uppercase tracking-wider text-muted-foreground truncate leading-none">
        {text}
      </span>
    </div>
  )
}

// ============================================================================
// COMPACT SLIDER — uses DS Slider size="sm"
// ============================================================================

const CompactSliderComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const min = parameter.minimum ?? 0
  const max = parameter.maximum ?? 100
  const step = parameter.type === 'integer' ? 1 : 0.01
  const safeValue = typeof value === 'number' ? value : (parameter.default as number) ?? min
  const displayValue = parameter.type === 'integer' ? safeValue : Number(safeValue.toFixed(2))

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%] flex items-center gap-1.5">
        <Slider
          value={[safeValue]}
          onValueChange={([val]) => onChange(val)}
          min={min}
          max={max}
          step={step}
          size="sm"
          className="flex-1 nodrag nopan"
        />
        <span className="font-mono text-[12px] text-muted-foreground tabular-nums min-w-[32px] text-right">
          {displayValue}
        </span>
      </div>
    </ControlRow>
  )
}
export const CompactSlider = React.memo(CompactSliderComponent)
CompactSlider.displayName = 'CompactSlider'

// ============================================================================
// COMPACT NUMBER — uses DS NumberInput
// ============================================================================

const CompactNumberComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const safeValue = typeof value === 'number' ? value : (parameter.default as number) ?? 0
  const step = parameter.type === 'integer' ? 1 : 0.1

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%]">
        <NumberInput
          value={safeValue}
          onChange={(v) => onChange(v)}
          min={parameter.minimum}
          max={parameter.maximum}
          step={step}
          displayDecimals={parameter.type === 'integer' ? 0 : 2}
          className="h-7 w-full text-[13px] border-border/50 bg-muted text-foreground nodrag nopan"
        />
      </div>
    </ControlRow>
  )
}
export const CompactNumber = React.memo(CompactNumberComponent)
CompactNumber.displayName = 'CompactNumber'

// ============================================================================
// COMPACT SELECT — uses DS Select with size="sm"
// ============================================================================

const CompactSelectComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const options = parameter.enum || []
  const currentValue = value !== undefined ? String(value) : String(parameter.default ?? '')

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%]">
        <Select
          value={currentValue}
          onValueChange={(val) => {
            onChange(
              parameter.type === 'number' || parameter.type === 'integer'
                ? Number(val)
                : val
            )
          }}
        >
          <SelectTrigger size="sm" className="h-7 font-mono text-[13px] border-border/50 bg-muted text-foreground hover:bg-black/[0.08] dark:hover:bg-white/[0.08]">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={String(option)} value={String(option)}>
                {String(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </ControlRow>
  )
}
export const CompactSelect = React.memo(CompactSelectComponent)
CompactSelect.displayName = 'CompactSelect'

// ============================================================================
// COMPACT TOGGLE — uses DS Switch size="sm"
// ============================================================================

const CompactToggleComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const checked = typeof value === 'boolean' ? value : Boolean(parameter.default)

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%]">
        <Switch
          checked={checked}
          onCheckedChange={(val) => onChange(val)}
          size="sm"
        />
      </div>
    </ControlRow>
  )
}
export const CompactToggle = React.memo(CompactToggleComponent)
CompactToggle.displayName = 'CompactToggle'

// ============================================================================
// COMPACT INPUT — uses DS-consistent input styling
// ============================================================================

const CompactInputComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const currentValue = value !== undefined ? String(value) : String(parameter.default ?? '')

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%]">
        <input
          type="text"
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={parameter.description || `Enter ${label.toLowerCase()}...`}
          className="flex w-full h-7 rounded-md border border-border/50 bg-muted px-2 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
        />
      </div>
    </ControlRow>
  )
}
export const CompactInput = React.memo(CompactInputComponent)
CompactInput.displayName = 'CompactInput'

// ============================================================================
// COMPACT SEED — uses DS NumberInput + randomize button
// ============================================================================

const CompactSeedComponent: React.FC<CompactControlProps> = ({
  label, value, onChange, parameter,
}) => {
  const safeValue = typeof value === 'number' ? value : (parameter.default as number) ?? 0
  const max = parameter.maximum ?? 2147483647
  const [spinning, setSpinning] = useState(false)

  const handleRandomize = useCallback(() => {
    onChange(Math.floor(Math.random() * max))
    setSpinning(true)
    setTimeout(() => setSpinning(false), 300)
  }, [onChange, max])

  return (
    <ControlRow>
      <div className="w-[38%] min-w-0">
        <CompactLabel text={label} title={parameter.description} icon={UI_TYPE_ICONS[parameter.uiType] || 'circle'} />
      </div>
      <div className="w-[62%] flex items-center gap-1">
        <NumberInput
          value={safeValue}
          onChange={(v) => onChange(v)}
          min={parameter.minimum ?? 0}
          max={max}
          step={1}
          className="h-7 flex-1 min-w-0 text-[13px] border-border/50 bg-muted text-foreground nodrag nopan"
        />
        <button
          type="button"
          onClick={handleRandomize}
          className="h-7 w-7 shrink-0 flex items-center justify-center rounded-md border border-border/50 bg-muted hover:bg-accent transition-colors"
          title="Randomize seed"
        >
          <svg
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground transition-transform duration-300',
              spinning && 'rotate-180'
            )}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M13 1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h10zM5 9.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm6-6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm0 6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm-6-6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" />
          </svg>
        </button>
      </div>
    </ControlRow>
  )
}
export const CompactSeed = React.memo(CompactSeedComponent)
CompactSeed.displayName = 'CompactSeed'
